import { useEffect, useMemo, useState } from 'react';
import type { Tag } from '@/types';
import type {
  EstadoFiltro,
  FiltrosCompromisos,
} from '@/hooks/useFiltrosCompromisos';
import type { MultiSelectOption } from './MultiSelectChip';
import styles from './FiltroAvanzadoDrawer.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
  filtros: FiltrosCompromisos;
  cantidadFiltrosActivos: number;
  // Setters expuestos por el hook (con cascada baked-in)
  setEstado: (e: EstadoFiltro) => void;
  setPlantas: (ids: string[]) => void;
  setPropuestas: (ids: string[]) => void;
  setIniciativas: (ids: string[]) => void;
  setEvs: (ids: string[]) => void;
  setTags: (ids: string[]) => void;
  limpiarTodo: () => void;
  // Opciones jerárquicas
  opcionesPlantas: MultiSelectOption[];
  opcionesPropuestas: MultiSelectOption[];
  opcionesIniciativas: MultiSelectOption[];
  opcionesEvs: MultiSelectOption[];
  tags: Tag[];
  // Estadísticas
  totalMostrados: number;
}

type Seccion = 'estado' | 'plantas' | 'propuestas' | 'iniciativas' | 'evs' | 'tags';

const TITULOS: Record<Seccion, string> = {
  estado: 'Estado',
  plantas: 'Plantas / Instalaciones',
  propuestas: 'Propuestas',
  iniciativas: 'Iniciativas',
  evs: 'Entregas de Valor',
  tags: 'Tags',
};

const ESTADO_OPCIONES: { id: EstadoFiltro; label: string; icon: string }[] = [
  { id: 'pendientes', label: 'Pendientes (no completados)', icon: '📋' },
  { id: 'bloqueados', label: 'Con bloqueos', icon: '🚫' },
  { id: 'completados', label: 'Completados', icon: '✓' },
  { id: 'todos', label: 'Todos', icon: '📂' },
];

export function FiltroAvanzadoDrawer({
  open,
  onClose,
  filtros,
  cantidadFiltrosActivos,
  setEstado,
  setPlantas,
  setPropuestas,
  setIniciativas,
  setEvs,
  setTags,
  limpiarTodo,
  opcionesPlantas,
  opcionesPropuestas,
  opcionesIniciativas,
  opcionesEvs,
  tags,
  totalMostrados,
}: Props) {
  const [expanded, setExpanded] = useState<Record<Seccion, boolean>>({
    estado: true,
    plantas: true,
    propuestas: false,
    iniciativas: false,
    evs: false,
    tags: false,
  });

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const tagOptions: MultiSelectOption[] = useMemo(
    () =>
      tags.map((t) => ({
        id: t.id,
        label: t.nombre,
        color: t.color,
      })),
    [tags],
  );

  if (!open) return null;

  return (
    <>
      <div className={styles.backdrop} onClick={onClose} />
      <aside
        className={styles.drawer}
        role="dialog"
        aria-modal="true"
        aria-labelledby="filtro-avanzado-title"
      >
        <div className={styles.header}>
          <div id="filtro-avanzado-title" className={styles.titleWrap}>
            ⚙ FILTRO AVANZADO
            {cantidadFiltrosActivos > 0 && (
              <span className={styles.headerCount}>
                {cantidadFiltrosActivos} filtro{cantidadFiltrosActivos === 1 ? '' : 's'} aplicado
                {cantidadFiltrosActivos === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>

        <div className={styles.body}>
          {/* Estado */}
          <Section
            seccion="estado"
            expanded={expanded.estado}
            onToggle={() =>
              setExpanded((p) => ({ ...p, estado: !p.estado }))
            }
            badge={filtros.estado !== 'pendientes' ? 1 : 0}
          >
            <div className={styles.list}>
              {ESTADO_OPCIONES.map((o) => (
                <label
                  key={o.id}
                  className={styles.item}
                  style={filtros.estado === o.id ? { background: 'var(--surface-2)' } : undefined}
                >
                  <input
                    type="radio"
                    name="estado"
                    checked={filtros.estado === o.id}
                    onChange={() => setEstado(o.id)}
                    style={{ accentColor: 'var(--brand)' }}
                  />
                  <span style={{ fontSize: 14 }}>{o.icon}</span>
                  <span>{o.label}</span>
                </label>
              ))}
            </div>
          </Section>

          {/* Plantas */}
          <Section
            seccion="plantas"
            expanded={expanded.plantas}
            onToggle={() => setExpanded((p) => ({ ...p, plantas: !p.plantas }))}
            badge={filtros.plantas.length}
          >
            <MultiSelectList
              options={opcionesPlantas}
              selected={filtros.plantas}
              onChange={setPlantas}
              singular="planta"
              plural="plantas"
            />
          </Section>

          {/* Propuestas */}
          <Section
            seccion="propuestas"
            expanded={expanded.propuestas}
            onToggle={() =>
              setExpanded((p) => ({ ...p, propuestas: !p.propuestas }))
            }
            badge={filtros.propuestas.length}
          >
            <MultiSelectList
              options={opcionesPropuestas}
              selected={filtros.propuestas}
              onChange={setPropuestas}
              singular="propuesta"
              plural="propuestas"
              emptyHint={
                filtros.plantas.length > 0
                  ? 'Ninguna propuesta para las plantas seleccionadas.'
                  : undefined
              }
            />
          </Section>

          {/* Iniciativas */}
          <Section
            seccion="iniciativas"
            expanded={expanded.iniciativas}
            onToggle={() =>
              setExpanded((p) => ({ ...p, iniciativas: !p.iniciativas }))
            }
            badge={filtros.iniciativas.length}
          >
            <MultiSelectList
              options={opcionesIniciativas}
              selected={filtros.iniciativas}
              onChange={setIniciativas}
              singular="iniciativa"
              plural="iniciativas"
            />
          </Section>

          {/* EVs */}
          <Section
            seccion="evs"
            expanded={expanded.evs}
            onToggle={() => setExpanded((p) => ({ ...p, evs: !p.evs }))}
            badge={filtros.evs.length}
          >
            <MultiSelectList
              options={opcionesEvs}
              selected={filtros.evs}
              onChange={setEvs}
              singular="entrega"
              plural="entregas"
            />
          </Section>

          {/* Tags */}
          <Section
            seccion="tags"
            expanded={expanded.tags}
            onToggle={() => setExpanded((p) => ({ ...p, tags: !p.tags }))}
            badge={filtros.tags.length}
          >
            <MultiSelectList
              options={tagOptions}
              selected={filtros.tags}
              onChange={setTags}
              singular="tag"
              plural="tags"
            />
          </Section>
        </div>

        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            <span>
              Mostrando <strong style={{ color: 'var(--text)' }}>{totalMostrados}</strong> compromisos
            </span>
            {cantidadFiltrosActivos > 0 && (
              <button
                type="button"
                className={styles.btnTextDanger}
                onClick={limpiarTodo}
              >
                Borrar todos los filtros
              </button>
            )}
          </div>
          <button type="button" className={styles.btnPrimary} onClick={onClose}>
            Aplicar y cerrar
          </button>
        </div>
      </aside>
    </>
  );
}

// ── Sub-componentes ──────────────────────────────────────────────────────

function Section({
  seccion,
  expanded,
  onToggle,
  badge,
  children,
}: {
  seccion: Seccion;
  expanded: boolean;
  onToggle: () => void;
  badge: number;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.section}>
      <button type="button" className={styles.sectionHeader} onClick={onToggle}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {TITULOS[seccion]}
          {badge > 0 && <span className={styles.sectionHeaderCount}>{badge}</span>}
        </span>
        <span style={{ fontSize: 12, opacity: 0.6 }}>{expanded ? '▾' : '▸'}</span>
      </button>
      {expanded && <div className={styles.sectionContent}>{children}</div>}
    </div>
  );
}

function MultiSelectList({
  options,
  selected,
  onChange,
  singular,
  plural,
  emptyHint,
}: {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  singular: string;
  plural: string;
  emptyHint?: string;
}) {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim();
    if (!term) return options;
    return options.filter((o) => o.label.toLowerCase().includes(term));
  }, [options, search]);

  function toggle(id: string) {
    if (selected.includes(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  }

  if (options.length === 0) {
    return (
      <div className={styles.empty}>
        {emptyHint || `No hay ${plural} disponibles.`}
      </div>
    );
  }

  return (
    <>
      {options.length > 5 && (
        <div className={styles.searchWrap}>
          <span className={styles.searchIcon}>🔍</span>
          <input
            type="search"
            className={styles.search}
            placeholder={`Buscar ${singular}…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}

      <div className={styles.actionsRow}>
        <button
          type="button"
          className={styles.actionBtn}
          onClick={() => onChange(options.map((o) => o.id))}
        >
          + Seleccionar todas
        </button>
        {selected.length > 0 && (
          <button
            type="button"
            className={styles.actionBtn}
            style={{ color: 'var(--text-soft)' }}
            onClick={() => onChange([])}
          >
            Limpiar
          </button>
        )}
      </div>

      <div className={styles.list}>
        {filtered.length === 0 && (
          <div className={styles.empty}>Sin resultados para "{search}"</div>
        )}
        {filtered.map((o) => (
          <label key={o.id} className={styles.item}>
            <input
              type="checkbox"
              checked={selected.includes(o.id)}
              onChange={() => toggle(o.id)}
            />
            {o.color && (
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: o.color,
                  flexShrink: 0,
                }}
              />
            )}
            <span>{o.label}</span>
          </label>
        ))}
      </div>
    </>
  );
}
